import { SideEnum, toBig, validatePrecision, type Security } from '@talos/kyoko';
import Papa from 'papaparse';
import { v4 as uuid } from 'uuid';
import { EMPTY_IMPORT_CARE_ORDER_ACTION, type ImportedCareOrder } from '../types';
import { getImportedCareOrdersWithActions } from '../utils';
import type { CareOrderImportContext, CareOrderImportParser } from './types';

const requiredHeaders = [
  'portfolio',
  'opendate',
  'transxtype',
  'quantity(9dps)',
  'description',
  'settledate',
  'tradingbenchmark',
  'settleccy',
  'ordnum',
] as const;
export type AladdinCsvRow = Record<(typeof requiredHeaders)[number], string> & {
  dircpty: string;
  tradingbenchmark: string;
};

/**
 * Parse a file coming from the Aladdin OMS, and create a ImportedCareOrder from each row.
 * @returns
 */
export const parse: CareOrderImportParser = async function parse(file, context) {
  const rows = await parseFile(file);
  const importedCareOrders = rows.map((row, index) => mapRow(row, { ...context, index }));
  return getImportedCareOrdersWithActions(importedCareOrders, context.allCareOrders, 'aladdin');
};

/**
 * Reads the file and parses it using PapaParse.
 */
function parseFile(file: File): Promise<AladdinCsvRow[]> {
  return new Promise((resolve, reject: (reason: string) => void) => {
    Papa.parse<AladdinCsvRow>(file, {
      delimiter: ',',
      header: true,
      skipEmptyLines: true,
      transformHeader: (header: string) => header.toLowerCase().replaceAll(' ', '').trim(),
      transform: (value: string) => value.trim(),
      complete: result => {
        if (!result.meta.fields) {
          return reject('Unable to parse header from file');
        }
        const missingHeader = requiredHeaders.find((key: string) => {
          return result.meta.fields?.indexOf(key.toLowerCase()) === -1;
        });
        if (missingHeader != null) {
          return reject(`Missing required header: ${missingHeader}`);
        }
        if (result.errors.length > 0) {
          return reject(result.errors.map(error => `Row ${error.row + 1} ${error.message} `).join(', '));
        }
        return resolve(result.data);
      },
    });
  });
}

// Just pick out the properties we care about here
type AladdinCareOrderImportContext = Pick<CareOrderImportContext, 'securitiesBySymbol'>;

/**
 * Maps the Aladdin CSV row to an ImportedCareOrder.
 */
export function mapRow(
  data: AladdinCsvRow,
  { securitiesBySymbol, index }: AladdinCareOrderImportContext & { index: number }
): ImportedCareOrder {
  const side = data.transxtype === 'BUY' ? SideEnum.Buy : SideEnum.Sell;

  const security = parseAladdinCsvSymbol(data, securitiesBySymbol);
  if (security == null) {
    throw new Error(`Row ${index + 1}: Invalid symbol`);
  }

  const currency = security?.PositionCurrency;
  const initiatingFrom = data.dircpty;

  const isCounterCurrency = data.settleccy === security.PositionCurrency;

  let orderQty: string | undefined;
  if (isCounterCurrency) {
    orderQty = toBig(data['quantity(9dps)'].replaceAll(/[-\s]/g, ''))?.toFixed();
    if (!validatePrecision(security.MinAmtIncrement ?? security.MinPriceIncrement, orderQty)) {
      throw new Error(
        `Row ${index + 1}: Invalid order qty "${orderQty ?? ''}", min increment is ${
          security.MinAmtIncrement ?? security.MinPriceIncrement
        }`
      );
    }
  } else {
    orderQty = toBig(data['quantity(9dps)'].replaceAll(/[-\s]/g, ''))?.toFixed();
    if (!validatePrecision(security.MinSizeIncrement, orderQty)) {
      throw new Error(
        `Row ${index + 1}: Invalid order qty "${orderQty ?? ''}", min increment is ${security.MinSizeIncrement}`
      );
    }
  }

  // It's still OK to add the order since `Group` isn't a unique field.
  const externalOrderID = data.ordnum?.trim() ?? '';
  if (externalOrderID === '') {
    throw new Error(`Row ${index + 1}: Missing required column "Order ID"`);
  }

  if (!orderQty) {
    throw new Error(`Row ${index + 1}: Missing required Quantity`);
  }

  if (!currency) {
    throw new Error(`Row ${index + 1}: Missing required Currency`);
  }

  return {
    ClOrdID: uuid(),
    Symbol: security.Symbol,
    Side: side,
    OrderQty: orderQty,
    Currency: currency,
    Annotations: initiatingFrom
      ? {
          ETP: {
            InitiatingFrom: initiatingFrom,
          },
        }
      : undefined,
    Group: externalOrderID,
    action: EMPTY_IMPORT_CARE_ORDER_ACTION,
  };
}

/**
 * Parse the symbol from the Aladdin CSV row.
 */
function parseAladdinCsvSymbol(data: AladdinCsvRow, securitiesBySymbol: Map<string, Security>): Security | undefined {
  try {
    const commodity = COMMODITY_SYMBOLS.get(data.description.toUpperCase());
    const currency = data.settleccy;

    if (commodity == null || currency == null) {
      return undefined;
    }

    const pair = `${commodity}-${currency}`;
    return securitiesBySymbol.get(pair);
  } catch (e) {
    return undefined;
  }
}

/**
 * The CSV's we get from Aladdin uses the "commodity name" as the symbol.
 * The mapping below is a very handwavy, hopefully correct, mapping from commodity name to symbol.
 */
const COMMODITY_SYMBOLS = new Map([
  ['BITCOIN', 'BTC'],
  ['ETHER', 'ETH'],
  ['LITECOIN', 'LTC'],
  ['RIPPLE', 'XRP'],
  ['CARDANO', 'ADA'],
  ['DOGECOIN', 'DOGE'],
  ['POLKADOT', 'DOT'],
  ['SOLANA', 'SOL'],
  ['TRON', 'TRX'],
  ['CHAINLINK', 'LINK'],
  ['UNISWAP', 'UNI'],
  ['AVALANCHE', 'AVAX'],
  ['POLYGON', 'MATIC'],
  ['BINANCECOIN', 'BNB'],
  ['TETHER', 'USDT'],
]);
